/**
 * Copy text to clipboard with optional HTML for rich paste support.
 *
 * Implementation strategy:
 * 1. If HTML provided and ClipboardItem supported: write both text/html and text/plain
 * 2. Else try modern Clipboard API (navigator.clipboard.writeText)
 * 3. Fallback to document.execCommand('copy') for older browsers
 *
 * @param text - The plain text to copy
 * @param html - Optional HTML version for rich paste (Word, Google Docs)
 * @returns Promise<boolean> - True if copy succeeded, false otherwise
 */
export async function copyToClipboard(text: string, html?: string): Promise<boolean> {
  // Try rich paste (HTML + plain text) if HTML provided and ClipboardItem supported
  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    try {
      const htmlBlob = new Blob([html], { type: 'text/html' })
      const textBlob = new Blob([text], { type: 'text/plain' })
      const item = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      })
      await navigator.clipboard.write([item])
      return true
    } catch (err) {
      console.warn('[Clipboard] Rich paste failed, trying plain text fallback:', err)
      // Continue to plain text fallback
    }
  }

  // Plain text fallback: Modern Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      console.warn('[Clipboard] Modern API failed, trying execCommand fallback:', err)
      // Continue to fallback
    }
  }

  // Final fallback for Safari, insecure contexts, and older browsers
  return copyToClipboardFallback(text)
}

/**
 * Fallback clipboard copy using document.execCommand('copy')
 * Creates a temporary textarea element to perform the copy operation.
 */
function copyToClipboardFallback(text: string): boolean {
  const textarea = document.createElement('textarea')

  // Style the textarea to be invisible but still functional
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '-9999px'
  textarea.setAttribute('readonly', '')

  document.body.appendChild(textarea)

  try {
    // Select the text
    textarea.select()
    textarea.setSelectionRange(0, text.length)

    // Execute copy command
    const success = document.execCommand('copy')

    return success
  } catch (err) {
    console.error('[Clipboard] Fallback copy failed:', err)
    return false
  } finally {
    // Always remove the temporary element
    document.body.removeChild(textarea)
  }
}
